import { Component, OnInit, ViewChild } from '@angular/core';
import * as d3 from 'd3';
import { DiscreteLegendComponent } from '../discrete-legend/discrete-legend.component';
import { LegendComponent } from '../legend/legend.component';
import { LayerItem, MapConfig } from '../models/map-config';
import { ColorType } from '../models/color-type';
import { HeatmapType } from '../models/heatmap-type';
import { TimestampTexts } from '../models/timestamp-texts';
import { TimespanService } from '../services/timespan/timespan.service';

/**
 * Agrupa legendas contínuas e discretas, e gerencia o estado delas.
 */
@Component({
  selector: 'app-legend-group',
  templateUrl: './legend-group.component.html',
  styleUrls: ['./legend-group.component.css']
})
export class LegendGroupComponent implements OnInit {

  /** Referência à legenda da chuva contínua. */
  @ViewChild("heatmapinter") legendinter : LegendComponent;

  /** Referência à legenda da chuva discreta. */
  @ViewChild("heatmapViasDiscrete") legendViasDiscrete: DiscreteLegendComponent;

  /** */
  @ViewChild("heatmapvias") legendvias: LegendComponent;

  /** Referência à legenda da contagem de ocorrências por célula de voronoi. */
  @ViewChild("voronoiEventsLegend") voronoiEventsLegend: LegendComponent;

  /** Referência à legenda do heatmap de ocorrências */
  @ViewChild("eventsHeatmapLegend") eventsHeatmapLegend: LegendComponent;

  /** Nomes das classes CSS atribuídas à legenda da chuva contínua. */
  heatmapLegendInter = ["heatmap-legend-rain-interpolado", "disabled"];

  /** Nomes das classes CSS atribuídas à legenda da chuva discreta. */
  legendViasDiscreteCSS = ["heatmap-legend-rain-vias", "disabled"];

  /** Nomes das classes CSS atribuídas à legenda das ocorrências. */
  eventsLegendCSS = ["events-legend", "disabled"];

  /** Nomes das classes CSS atribuídas à legenda da contagem de ocorrências por célula de voronoi. */
  voronoiEventsLegendCSS = ["voronoi-events-legend", "disabled"];

  /** Nome das classes CSS atribuídas à legenda das câmeras. */
  legendCamerasCSS = ["cameras-legend", "disabled"];

  eventsHeatmapLegendCSS = ["events-heatmap-legend", "disabled"];

  /** Indica se a legenda da camada das rotas da heurística está ativa ou não. */
  isRoutesLegendActive = false;

  /**
   * Limites de chuva em milímetros por hora (mm/h)
   * 
   * fraca:    chuva <  2.5
   * moderada: 2.5   >= chuva < 10
   * forte:    10    >= chuva < 50
   * violenta: chuva >= 50
   */
  rainThresholds: number[] = [2.5, 10, 50];

  /** Nomes das colunas dos acumulados de chuva como são no BD e como devem
   * ser exibidos na interface.
   */
  timeColumns: {name: string, text: string}[] = [
    { name:'indice_pluv', text: 'Agora'},
    { name: 'quinzemin', text: '15 minutos'},
    { name: 'trintamin', text: '30 minutos'},
    { name: 'umahora', text: '1 Hora'},
    { name: 'seishoras', text: '6 Horas'},
    { name: 'dozehoras', text: '12 Horas'},
    { name: 'vintequatrohoras', text: '24 horas'},
    { name: 'quarentaoitohoras', text: '48 Horas'},
    { name: 'setentaduashoras', text: '72 Horas'},
    { name: 'noventaseishoras', text: '96 Horas'},
    { name: 'mes', text: '1 mês'}
  ];

  /**  */
  columnSpecialArgs = {
    'percent': new Set(['percent']),
    'time': new Set(['total', 'nfraca', 'nmoderada', 'nforte', 'nmuitoforte']),
  };

  /** Partes dos títulos da legenda de chuva. */
  titles: {[key: string]: string[]} = {
    'percent': ['PORCENTAGEM', 'DE CHUVA'],
    'nfraca': ['DURAÇÃO DA', 'CHUVA FRACA'],
    'nmoderada': ['DURAÇÃO DA', 'CHUVA MODERADA'],
    'nforte': ['DURAÇÃO DA', 'CHUVA FORTE'],
    'nmuitoforte': ['DURAÇÃO DA CHUVA', 'MUITO FORTE'],
    'mean': ['MÉDIA'],
    'mode': ['MODA'],
    'median': ['MEDIANA'],
  }

  /** Cores usadas na coloração discreta do mapa:
   * 0: fraca
   * 1: moderada
   * 2: forte
   * 3: muito forte
   */
  discreteColors = ['#339944', '#FFCC00', '#FF8822', '#DD2233'];

  /** Linhas de título para a legenda discreta de chuva. */
  discreteRainTitleLines: string[] = [];

  /** Textos dos itens para a legenda discreta de chuva */
  discreteRainTexts: string[] = [];

  /** Textos de identificação das rotas na legenda da camada das rotas da heurística. */
  routesTexts: string[] = [];


  constructor(protected timestampService: TimespanService) { }

  ngOnInit(): void {
  }

  /**
   * Atualiza as legendas dos dados das chuvas, contínua e discreta.
   * @param mapConfig 
   */
  updateRainLegends(mapConfig: MapConfig) {

    let activeColors = new Set();
    let { middleIndicesPluv, selectedColumn } = mapConfig.geoData;
    let { list } = mapConfig.layer;
    let rainLegends = [HeatmapType.STATIONS, HeatmapType.HEATMAP, 
      HeatmapType.HIGHWAYS, HeatmapType.RIVERS];

    // Define se as camadas contínua e discreta precisam ser ativadas
    list.forEach((item: LayerItem, index: number) => {

      let { color, isActive } = item;

      if (isActive === false || color == null || !rainLegends.includes(index)) {
        return;
      }

      activeColors.add(color);
    });

    this.toggleContinuousLegend(activeColors, mapConfig);
    this.toggleDiscreteLegend(activeColors, mapConfig);
  }

  /**
   * Alterna entre exibir e esconder a legenda de chuva contínua.
   * @param activeColors conjunto das colorações (contínua, discreta) da legenda de chuva que estão ativas.
   * @param mapConfig tem os dados a serem exibidos na legenda.
   */
  private toggleContinuousLegend(activeColors: Set<ColorType>, mapConfig: MapConfig) {

    let { middleIndicesPluv, selectedColumn } = mapConfig.geoData;

    if (middleIndicesPluv && activeColors.has(ColorType.CONTINUOUS)) {
      this.setContinuousLegend(
        this.legendinter, 
        this.getLegendTitle(mapConfig),
        middleIndicesPluv,
        this.getSpecialArgs(selectedColumn)
      );
      this.heatmapLegendInter = ["heatmap-legend-rain-interpolado", "enabled"];
    }
    else {
      this.heatmapLegendInter = ["heatmap-legend-rain-interpolado", "disabled"];
    }
  }

  /**
   * Configura a legenda contínua.
   * @param legend componente de legenda a ser configurado.
   * @param titleLines linhas do título da legenda.
   * @param values valores a serem exibidos na legenda.
   * @param valueType tipo de formatação dos valores.
   * @param colorScale função de coloração da barra da legenda.
   */
  private setContinuousLegend(
    legend: LegendComponent, 
    titleLines: string[], 
    values: number[],
    valueType: string,
    colorScale: (t: number) => string = d3.interpolateBlues) {
  
    legend.updateColor(colorScale);
  
    let maxVal = values[values.length - 1];
    let minVal = values[0];
  
    let maxValText = this.formatLegendValue(maxVal, valueType);
    let minValText = this.formatLegendValue(minVal, valueType);
  
    legend.insertText(titleLines, maxValText, minValText);
  }
  
  /**
   * Alterna a exibição da legenda de chuva discreta.
   * @param activeColors conjunto das colorações (contínua, discreta) da legenda de chuva que estão ativas.
   * @param mapConfig tem os dados da chuva a serem usados na legenda.
   */
  private toggleDiscreteLegend(activeColors: Set<ColorType>, mapConfig: MapConfig) {
    
    let { filteredStations } = mapConfig.geoData;
    let noStationsRainData = filteredStations == null || filteredStations.features.length == 0;
    let noActiveDiscreteLayers = !activeColors.has(ColorType.DISCRETE);

    if (noStationsRainData || noActiveDiscreteLayers) {
      this.legendViasDiscreteCSS = ["heatmap-legend-rain-vias", "disabled"];
      return;
    }

    // Cria array de textos da legenda discreta para a resolução de tempo selecionada.
    const ltUnicode = '\u003C';
    const gteUnicode = '\u2265';
    const multiplier = TimestampTexts.multipliers[mapConfig.geoData.selectedColumn];
    const rainValues = this.rainThresholds.map(val => `${ltUnicode} ${val * multiplier}`)
    const last = this.rainThresholds[this.rainThresholds.length - 1] * multiplier;
    rainValues.push(`${gteUnicode} ${last}`);

    this.discreteRainTitleLines = this.getLegendTitle(mapConfig);
    this.discreteRainTexts = rainValues;
    this.legendViasDiscreteCSS = ["heatmap-legend-rain-vias", "enabled"];

  }


  /**
   * Formata o valor para exibir na parte inferior ou superior da legenda.
   * 
   * @param value valor a ser formatado
   * @param valueType caracter que determina o tipo de formatação
   * @returns valor formatado
   */
  private formatLegendValue(value: number, valueType: string) {
    if (valueType == '%') {
      return `${value.toFixed(2)} ${valueType}`;
    }
    if (valueType == 'dhm') {
      return TimespanService.secondsToFormattedText(value);
    }
    if (valueType == 'int') {
      return value.toFixed(0);
    }
    return value.toFixed(2);
  }

  /**
   * Obtém o título a ser usado em uma legenda de chuva.
   * @returns array no qual cada string é uma linha na legenda.
   */
  private getLegendTitle(mapConfig: MapConfig): string[] {

    let { selectedColumn } = mapConfig.geoData;

    selectedColumn = (selectedColumn == "indice_pluv") 
      ? "quinzemin"
      : selectedColumn;

    let timestampText = this.getTimestampText(selectedColumn);
    let [value, unit] = timestampText.split(" ");

    if (unit == "minutos") {
      unit = "min";
    }
    else if (unit == "mês") {
      unit = "mês";
    }
    else {
      unit = "h";
    }

    let lines = (selectedColumn in this.titles) ? 
      this.titles[selectedColumn] : ["ACÚMULO", "DE CHUVA"];
    
    if (selectedColumn != "percent") {
      lines.push(`(mm / ${value} ${unit})`);
    }
  
    return lines;
  }

  /**
   * Obtém o tipo de formatação de acordo com o nome do atributo selecionado 
   * para ser exibido na legenda contínua.
   * @param selectedColumn nome da coluna a ser usada na legenda.
   * @returns 
   */
  private getSpecialArgs(selectedColumn: string): string {
    
    if (this.columnSpecialArgs.percent.has(selectedColumn)) {
      return '%';
    }

    if (this.columnSpecialArgs.time.has(selectedColumn)) {
      return 'dhm';
    }

    return '';
  }

  /**
   * Obtém o texto a ser exibido para a coluna do agregado 
   * de chuvas selecionada.
   * @param timestamp nome da coluna do agregado de chuva selecionada.
   * @returns texto correspondente ao agregado de chuva selecionado.
   */
  private getTimestampText(timestamp: string){
    let text = ""
    this.timeColumns.forEach(val=>{
      if (val.name == timestamp)  text = val.text
    })
    return text
  }

  /**
   * Alterna a exibição da legenda das ocorrências.
   * @param active indica se a legenda deve ser exibida ou escondida.
   */
  updateEventsLegend(active: boolean) {

    this.eventsLegendCSS[1] = active ? "enabled" : "disabled";
    this.eventsLegendCSS = this.eventsLegendCSS;
  }

  /**
   * Alterna entre exibir e esconder a legenda das câmeras.
   * @param active indica se a legenda deve ser exibida ou escondida.
   */
  updateCameraLegend(active: boolean) {
    // throw new Error('Method not implemented.');
    this.legendCamerasCSS[1] = active ? "enabled" : "disabled";
    this.legendCamerasCSS = this.legendCamerasCSS;
  }

  /**
   * Alterna entre exibir e esconder a legenda da camada de voronoi.
   * @param active indica se a legenda será exibida ou escondida.
   * @param middleCounts tem os valores a serem exibidos na legenda.
   */
  updateVoronoiLegend(active: boolean, middleCounts: number[]) {

    if (middleCounts == null || middleCounts.length == 0) {
      this.voronoiEventsLegendCSS[1] = "disabled";
      this.voronoiEventsLegendCSS = this.voronoiEventsLegendCSS;
      return;
    }
    
    this.voronoiEventsLegendCSS[1] = active ? "enabled" : "disabled";
    this.voronoiEventsLegendCSS = this.voronoiEventsLegendCSS;

    this.setContinuousLegend(this.voronoiEventsLegend, ["OCORRÊNCIAS"], 
      middleCounts, "int", d3.interpolatePuRd);
  }

  /**
   * Alterna a exibição da legenda do heatmap de ocorrências.
   * @param active indica se a legenda será exibida ou escondida.
   * @param middleCounts tem os valores a serem exibidos na legenda.
   * @returns 
   */
  updateEventsHeatmapLegend(active: boolean, middleCounts: number[]) {

    if (middleCounts == null || middleCounts.length == 0) {
      this.eventsHeatmapLegendCSS[1] = "disabled";
      this.eventsHeatmapLegendCSS = this.eventsHeatmapLegendCSS;
      return;
    }
    
    this.eventsHeatmapLegendCSS[1] = active ? "enabled" : "disabled";
    this.eventsHeatmapLegendCSS = this.eventsHeatmapLegendCSS;

    this.setContinuousLegend(this.eventsHeatmapLegend, ["OCORRÊNCIAS"], 
      middleCounts, "int", d3.interpolateYlOrBr);
  }

  /**
   * Atualiza a legenda da camada das rotas da heurística.
   * 
   * @param active indica se a camada está ativa ou não.
   * @param texts labels para exibir na legenda. Cada label identifica uma rota.
   */
  updateRoutesLegend(active: boolean, texts: string[]) {
    this.isRoutesLegendActive = active;
    this.routesTexts = texts;
  }
}
